'use client'

import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/ui/atoms/dialog/Dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/ui/atoms/dropdown/DropdownMenu'
import { MobileTokenFilter } from '@/ui/molecules/token-filter/MobileTokenFilter'
import { TokenFilterDialog } from '@/ui/molecules/token-filter/TokenFilterDialog'
import { cn } from '@/ui/utils/style'
import { ExpandableSearch } from '@/ui/atoms/expandable-search/ExpandableSearch'
import { ArrowDownWideNarrow, ArrowUpNarrowWide, CircleX, SlidersHorizontal, Wallet } from 'lucide-react'
import type { AssetFilter, MarketFilter, UnifiedEarnFilter } from '../types'
import { ASSET_FILTERS, MARKET_FILTERS, SORT_FIELDS } from '../types'

interface UnifiedEarnFilterBarProps {
  filter: UnifiedEarnFilter
  onFilterChange: (filter: UnifiedEarnFilter) => void
  hasWallet?: boolean
  assetOptions?: string[]
  assetAddressBySymbol?: Record<string, string>
}

export function UnifiedEarnFilterBar({
  filter,
  onFilterChange,
  hasWallet,
  assetOptions = [],
  assetAddressBySymbol,
}: UnifiedEarnFilterBarProps) {
  const isAllSelected = filter.markets.length === 0 && filter.assets.length === 0 && filter.assetFilter.length === 0

  const toggleMarket = (market: MarketFilter) => {
    const newMarkets = filter.markets.includes(market)
      ? filter.markets.filter((m) => m !== market)
      : [...filter.markets, market]
    onFilterChange({ ...filter, markets: newMarkets })
  }

  const toggleAsset = (asset: AssetFilter) => {
    const newAssets = filter.assets.includes(asset)
      ? filter.assets.filter((a) => a !== asset)
      : [...filter.assets, asset]
    onFilterChange({ ...filter, assets: newAssets })
  }

  const clearFilters = () => {
    onFilterChange({ ...filter, markets: [], assets: [], assetFilter: [] })
  }

  const activeFilterCount = filter.markets.length + filter.assets.length + (filter.assetFilter.length > 0 ? 1 : 0)

  return (
    <div className="sticky top-12 z-50 flex flex-wrap items-center justify-between gap-2 bg-background py-2 lg:static lg:gap-4 lg:bg-transparent lg:py-0">
      <div className="flex items-center gap-2 lg:flex-wrap">
        <div className="hidden lg:block">
          <FilterPill selected={isAllSelected} onClick={clearFilters}>
            All
          </FilterPill>
        </div>
        <div className="mx-1 hidden h-4 w-px shrink-0 bg-white/10 lg:block" />
        <div className="hidden items-center gap-2 lg:flex">
          {MARKET_FILTERS.map((market) => (
            <FilterPill key={market} selected={filter.markets.includes(market)} onClick={() => toggleMarket(market)}>
              {market}
            </FilterPill>
          ))}
          <div className="mx-1 h-4 w-px bg-white/10" />
          {ASSET_FILTERS.map((asset) => (
            <FilterPill key={asset} selected={filter.assets.includes(asset)} onClick={() => toggleAsset(asset)}>
              {asset}
            </FilterPill>
          ))}
          {assetOptions.length > 0 && (
            <>
              <div className="mx-1 h-4 w-px bg-white/10" />
              <TokenFilterDialog
                options={assetOptions}
                selected={filter.assetFilter}
                onSelect={(symbols) => onFilterChange({ ...filter, assetFilter: symbols })}
                title="Asset"
                tokenAddressBySymbol={assetAddressBySymbol}
              />
            </>
          )}
        </div>
        <div className="lg:hidden">
          <Dialog>
            <DialogTrigger asChild>
              <button
                type="button"
                className={cn(
                  'relative flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 font-medium text-sm transition-colors',
                  activeFilterCount > 0
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-foreground',
                )}
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
                <span>Filters</span>
                {activeFilterCount > 0 && (
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white/20 text-xs">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </DialogTrigger>
            <DialogContent className="p-4 pt-16 sm:p-6" aria-describedby={undefined}>
              <div className="space-y-4">
                <div className="flex items-center gap-2 pr-8">
                  <DialogTitle className="pr-2 text-lg">Filters</DialogTitle>
                  {activeFilterCount > 0 && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="flex items-center gap-1 rounded bg-secondary p-0.5 px-2 text-muted-foreground text-xs hover:text-foreground"
                    >
                      <CircleX className="h-3 w-3" /> Clear all filters
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="font-medium text-muted-foreground text-xs uppercase tracking-wider">Markets</div>
                  <div className="flex flex-wrap gap-1.5">
                    {MARKET_FILTERS.map((market) => (
                      <FilterPill
                        key={market}
                        selected={filter.markets.includes(market)}
                        onClick={() => toggleMarket(market)}
                        size="sm"
                      >
                        {market}
                      </FilterPill>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="font-medium text-muted-foreground text-xs uppercase tracking-wider">Assets</div>
                  <div className="flex flex-wrap gap-1.5">
                    {ASSET_FILTERS.map((asset) => (
                      <FilterPill
                        key={asset}
                        selected={filter.assets.includes(asset)}
                        onClick={() => toggleAsset(asset)}
                        size="sm"
                      >
                        {asset}
                      </FilterPill>
                    ))}
                  </div>
                </div>
                {assetOptions.length > 0 && (
                  <div className="space-y-2">
                    <div className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                      Token Filter
                    </div>
                    <MobileTokenFilter
                      options={assetOptions}
                      selected={filter.assetFilter}
                      onSelect={(symbols) => onFilterChange({ ...filter, assetFilter: symbols })}
                      tokenAddressBySymbol={assetAddressBySymbol}
                    />
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="ml-auto flex items-center gap-2">
        <ExpandableSearch
          value={filter.searchQuery}
          onChange={(query) => onFilterChange({ ...filter, searchQuery: query })}
        />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex h-9 items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 text-muted-foreground transition-colors lg:hidden hover:text-foreground"
              title="Sort options"
            >
              {filter.sortDirection === 'desc' ? (
                <ArrowDownWideNarrow className="h-4 w-4" />
              ) : (
                <ArrowUpNarrowWide className="h-4 w-4" />
              )}
              <span className="font-medium text-xs">{filter.sortBy === 'tvl' ? 'TVL' : 'APY'}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-36 p-2">
            <div className="flex flex-col gap-2">
              <div className="font-medium text-muted-foreground text-xs uppercase tracking-wider">Sort by</div>
              <div className="flex flex-wrap gap-1.5">
                {SORT_FIELDS.map((field) => (
                  <SortPill
                    key={field.value}
                    selected={filter.sortBy === field.value}
                    onClick={() => onFilterChange({ ...filter, sortBy: field.value })}
                  >
                    {field.label}
                  </SortPill>
                ))}
              </div>
              <div className="font-medium text-muted-foreground text-xs uppercase tracking-wider">Order</div>
              <div className="flex flex-wrap gap-1.5">
                <SortPill
                  selected={filter.sortDirection === 'desc'}
                  onClick={() => onFilterChange({ ...filter, sortDirection: 'desc' })}
                >
                  High → Low
                </SortPill>
                <SortPill
                  selected={filter.sortDirection === 'asc'}
                  onClick={() => onFilterChange({ ...filter, sortDirection: 'asc' })}
                >
                  Low → High
                </SortPill>
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {hasWallet && (
          <div className="flex h-9 items-center rounded-lg border border-white/10 bg-white/5">
            <button
              type="button"
              onClick={() => {
                onFilterChange({
                  ...filter,
                  inWalletOnly: !filter.inWalletOnly,
                })
              }}
              className={cn(
                'flex h-full items-center justify-center px-2.5 transition-colors',
                filter.inWalletOnly ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              )}
              title="Show assets in wallet"
            >
              <Wallet className="h-4 w-4" />
            </button>
            <div className="h-4 w-px bg-white/10" />
            <button
              type="button"
              onClick={() => {
                onFilterChange({
                  ...filter,
                  depositedOnly: !filter.depositedOnly,
                })
              }}
              className={cn(
                'group flex h-full items-center justify-center px-2.5 transition-colors',
                filter.depositedOnly ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              )}
              title="Show my positions"
            >
              <span
                className={cn(
                  'text-sm transition-all',
                  !filter.depositedOnly &&
                    'opacity-50 contrast-0 grayscale group-hover:opacity-100 group-hover:brightness-200',
                )}
                style={
                  filter.depositedOnly
                    ? {
                        background: 'linear-gradient(90deg, #FCE572 0%, #A1FCE7 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }
                    : undefined
                }
              >
                💰
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

interface FilterPillProps {
  children: React.ReactNode
  selected: boolean
  onClick: () => void
  size?: 'sm' | 'default'
}

function FilterPill({ children, selected, onClick, size = 'default' }: FilterPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'shrink-0 rounded-full font-medium transition-colors',
        size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm',
        selected
          ? 'bg-primary text-primary-foreground'
          : 'bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}

interface SortPillProps {
  children: React.ReactNode
  selected: boolean
  onClick: () => void
}

function SortPill({ children, selected, onClick }: SortPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'shrink-0 rounded-full px-2.5 py-1 font-medium text-xs transition-colors',
        selected
          ? 'bg-primary text-primary-foreground'
          : 'bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}
